/** Display preferences stored on this device (see `ui.store.ts`). */

export const DATE_FORMATS = ["mdy", "dmy", "ymd"] as const;
export type DateFormatPreference = (typeof DATE_FORMATS)[number];

export const TIME_FORMATS = ["12h", "24h"] as const;
export type TimeFormatPreference = (typeof TIME_FORMATS)[number];

export const WEEK_STARTS = ["sunday", "monday"] as const;
export type WeekStartPreference = (typeof WEEK_STARTS)[number];

export const ACCENTS = ["indigo", "blue", "teal", "green", "orange", "rose", "violet"] as const;
export type AccentPreference = (typeof ACCENTS)[number];

export const DATE_FORMAT_LABELS: Record<DateFormatPreference, string> = {
  mdy: "mm/dd/yyyy",
  dmy: "dd/mm/yyyy",
  ymd: "yyyy/mm/dd",
};

export const TIME_FORMAT_LABELS: Record<TimeFormatPreference, string> = {
  "12h": "12 hour",
  "24h": "24 hour",
};

export const WEEK_START_LABELS: Record<WeekStartPreference, string> = {
  sunday: "Sunday",
  monday: "Monday",
};

export function isOption<T extends string>(options: readonly T[], value: unknown): value is T {
  return options.some((option) => option === value);
}

/** First-visit defaults taken from the browser's locale. */
export function browserDefaults(): {
  dateFormat: DateFormatPreference;
  timeFormat: TimeFormatPreference;
} {
  try {
    const sample = new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
    const order = sample
      .formatToParts(new Date(2026, 9, 25))
      .filter((part) => part.type === "year" || part.type === "month" || part.type === "day")
      .map((part) => part.type[0])
      .join("");
    const hour12 = new Intl.DateTimeFormat(undefined, { hour: "numeric" }).resolvedOptions().hour12;
    return {
      dateFormat: order === "ymd" ? "ymd" : order === "dmy" ? "dmy" : "mdy",
      timeFormat: hour12 === false ? "24h" : "12h",
    };
  } catch {
    return { dateFormat: "mdy", timeFormat: "12h" };
  }
}

export const ACCENT_LABELS: Record<AccentPreference, string> = {
  indigo: "Indigo",
  blue: "Blue",
  teal: "Teal",
  green: "Green",
  orange: "Orange",
  rose: "Rose",
  violet: "Violet",
};

/** OKLCH hue for each accent. Indigo is the stylesheet default, so it needs no override. */
export const ACCENT_HUES: Record<Exclude<AccentPreference, "indigo">, number> = {
  blue: 255,
  teal: 190,
  green: 150,
  orange: 50,
  rose: 15,
  violet: 305,
};

/** Swatch color matching what `index.css` derives from the hue in light mode. */
export function accentSwatch(accent: AccentPreference): string {
  return accent === "indigo"
    ? "oklch(0.511 0.262 276.966)"
    : `oklch(0.55 0.2 ${String(ACCENT_HUES[accent])})`;
}
